import { integer, uuid, varchar, boolean, timestamp, text } from 'drizzle-orm/pg-core/columns';
import { relations } from 'drizzle-orm/relations';
import { pgTable } from 'drizzle-orm/pg-core/table';
import { type InferSelectModel } from 'drizzle-orm/table';
import { index, uniqueIndex } from 'drizzle-orm/pg-core/indexes';
import { byteaAsHexString } from './types-bytea';
import { authKeys, companySuggestions, fidoKeys, webAuthnKeys } from './initial';
import { companyInvites } from './company-invites';
import { companyUsers } from './company-users';
import { commonFields } from './common-fields';
import { identities } from './identities';

export type User = InferSelectModel<typeof users>;

export enum ReferralSource {
  STARTUPTOOLS = 'STARTUPTOOLS',
  GOOGLE = 'GOOGLE',
  LINKEDIN = 'LINKEDIN',
  FRIEND = 'FRIEND',
  MARKETING = 'MARKETING',
  EVENT = 'EVENT',
  BOLAGO = 'BOLAGO',
  OTHER = 'OTHER',
}

export const users = pgTable(
  'users',
  {
    ...commonFields,
    // encryptedPassword: varchar('encrypted_password'), - Should not be exposed anywhere but on auth-service
    resetPasswordToken: varchar('reset_password_token'),
    resetPasswordSentAt: timestamp('reset_password_sent_at'),
    signInCount: integer('sign_in_count').default(0).notNull(),
    currentSignInAt: timestamp('current_sign_in_at'),
    lastSignInAt: timestamp('last_sign_in_at'),
    currentSignInIp: varchar('current_sign_in_ip'),
    lastSignInIp: varchar('last_sign_in_ip'),
    confirmationToken: varchar('confirmation_token'),
    confirmedAt: timestamp('confirmed_at'),
    confirmationSentAt: timestamp('confirmation_sent_at'),
    unconfirmedEmail: varchar('unconfirmed_email'),
    failedAttempts: integer('failed_attempts').default(0).notNull(),
    unlockToken: varchar('unlock_token'),
    lockedAt: timestamp('locked_at', { mode: 'string' }),
    email: varchar(),
    admin: boolean().default(false).notNull(),
    companySuggestionsSyncedDate: timestamp('company_suggestions_synced_date'),
    personalId: varchar('personal_id'),
    jwtEpoch: integer('jwt_epoch').default(1).notNull(),
    // UNUSED authMethod: integer('auth_method').notNull(),
    name: varchar(),
    emailMd5: byteaAsHexString('email_md5'),
    phone: varchar(),
    communicationLanguage: varchar('communication_language').default('sv').notNull(),
    // UNUSED intercomHmac: bytea('intercom_hmac'),
    hubspotObjectId: varchar('hubspot_object_id'),
    truidId: uuid('truid_id'),
    referralSource: text().$type<ReferralSource>(),
    referralSourceOther: text(),
  },
  table => {
    return [
      {
        indexUsersOnConfirmationToken: uniqueIndex('index_users_on_confirmation_token').using(
          'btree',
          table.confirmationToken.asc().nullsLast().op('text_ops'),
        ),
        indexUsersOnEmail: index('index_users_on_email').using('btree', table.email.asc().nullsLast().op('text_ops')),
        indexUsersOnPersonalId: uniqueIndex('index_users_on_personal_id').using(
          'btree',
          table.personalId.asc().nullsLast().op('text_ops'),
        ),
        indexUsersOnResetPasswordToken: uniqueIndex('index_users_on_reset_password_token').using(
          'btree',
          table.resetPasswordToken.asc().nullsLast().op('text_ops'),
        ),
        indexUsersOnUnlockToken: uniqueIndex('index_users_on_unlock_token').using(
          'btree',
          table.unlockToken.asc().nullsLast().op('text_ops'),
        ),
      },
    ];
  },
);

export const usersRelations = relations(users, ({ many }) => ({
  authKeys: many(authKeys),
  companyInvites: many(companyInvites),
  companySuggestions: many(companySuggestions),
  companyUsers: many(companyUsers),
  fidoKeys: many(fidoKeys),
  identities: many(identities),
  webAuthnKeys: many(webAuthnKeys),
}));
